import { useMonitoringFile } from "@/context/MonitoringFileContext";
import { useAppHref } from "@/lib/path-utils";
import { PROMPT_LANGUAGE_ID } from "@/lib/prompt/monaco";
import { RadixAppDialog } from "@components/library/AppDialog";
import IconButton from "@components/library/IconButton";
import { ArrowUpRightIcon } from "@components/library/Icons";
import { hlToastApiError } from "@components/library/Toast";
import Tooltip from "@components/library/Tooltip";
import { InputLabel } from "@components/molecules/FormikInput";
import { parseAsString, useQueryState } from "nuqs";
import { useCallback, useEffect, useState } from "react";
import { DiffViewer } from "../Code/DiffViewer";
import VersionSelect from "./VersionSelect";
import { serializeFileById } from "@/services/files.service";

interface Props {
  open: boolean;
  onClose: () => void;
}

export const DIFF_QUERY_ORIGINAL = "original";
export const DIFF_QUERY_MODIFIED = "modified";

export const VersionDiffModal = ({ open, onClose }: Props) => {
  // In the future, I think this should independent of files... in fact you should just be able to filepick any version and compare.
  const { queryFileId } = useMonitoringFile();
  const [originalId, setOriginalId] = useQueryState(DIFF_QUERY_ORIGINAL, parseAsString);
  const [modifiedId, setModifiedId] = useQueryState(DIFF_QUERY_MODIFIED, parseAsString);
  const [original, setOriginal] = useState<string | null>(null);
  const [modified, setModified] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const fetchAndSetContent = useCallback(
    async (newVersionId: string, setContent: (content: string | null) => void) => {
      setIsLoading(true);
      try {
        if (!queryFileId) {
          return;
        }
        const serialized = await serializeFileById(queryFileId, { versionId: newVersionId });
        setContent(serialized.data);
      } catch (error) {
        hlToastApiError({ error, titleFallback: "Could not serialize Version" });
      } finally {
        setIsLoading(false);
      }
    },
    [queryFileId],
  );

  useEffect(() => {
    if (originalId) {
      fetchAndSetContent(originalId, setOriginal);
    }
  }, [originalId, fetchAndSetContent]);
  useEffect(() => {
    if (modifiedId) {
      fetchAndSetContent(modifiedId, setModified);
    }
  }, [modifiedId, fetchAndSetContent]);

  return (
    <RadixAppDialog
      open={open}
      onClose={onClose}
      className="flex h-[90vh] !w-[90vw] flex-col"
      title={"Compare Prompts"}
    >
      <div className="flex">
        <div className="flex h-40 grow items-center gap-24 [@media(min-width:1076px)]:gap-0">
          <div className="[@media(min-width:1076px)]:grow">
            <VersionDropdownAndActions
              title="Reference"
              selectOnChange={(versionId) => setOriginalId(versionId)}
              versionId={originalId}
            />
          </div>
          <div className="[@media(min-width:1076px)]:grow">
            <VersionDropdownAndActions
              title="Modified"
              selectOnChange={(versionId) => setModifiedId(versionId)}
              versionId={modifiedId}
            />
          </div>
        </div>
      </div>

      <div className="relative flex grow flex-col border border-stroke-base-3">
        {originalId && modifiedId && original !== null && modified !== null && (
          <DiffViewer
            key={originalId + modifiedId + isLoading}
            original={original}
            modified={modified}
            language={PROMPT_LANGUAGE_ID}
          />
        )}
        {isLoading && (
          <div className="absolute inset-0  flex grow items-center justify-center bg-gray-100/90 text-gray-400">
            Loading...
          </div>
        )}
      </div>
    </RadixAppDialog>
  );
};

const VersionDropdownAndActions = ({
  title,
  selectOnChange,
  versionId,
}: {
  title: string;
  selectOnChange: (id: string | null) => void;
  versionId: string | null;
}) => {
  const { queryFileId } = useMonitoringFile();
  const href =
    useAppHref({
      id: queryFileId!,
      page: "dashboard",
      type: "file",
    }) + `?id=${versionId}`;
  return (
    <div className="flex grow items-end gap-8">
      <div className="flex flex-col gap-8">
        <InputLabel name={title} label={title} />
        <VersionSelect fileId={queryFileId!} versionId={versionId} onChange={selectOnChange} />
      </div>
      <div className="mb-2 flex items-center gap-8">
        <Tooltip content="View Prompt">
          <IconButton Icon={ArrowUpRightIcon} href={href} target="_blank" />
        </Tooltip>
      </div>
    </div>
  );
};
